import { memo, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  ScrollArea,
} from '@flote/ui';
import { RecipientForm } from './RecipientForm';
import type { Recipient, CreateRecipientRequest, UpdateRecipientRequest } from '@flote/contracts/http';

interface RecipientFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CreateRecipientRequest | UpdateRecipientRequest) => Promise<void>;
  isProcessing?: boolean;
  recipient?: Recipient;
}

export const RecipientFormModal = memo<RecipientFormModalProps>(({
  open,
  onOpenChange,
  onSubmit,
  isProcessing = false,
  recipient,
}) => {
  const isEditMode = !!recipient;

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && !isProcessing) {
        onOpenChange(false);
      }
    },
    [onOpenChange, isProcessing]
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-2xl max-h-[90vh] p-0 sm:w-full">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle>{isEditMode ? 'Edit Recipient' : 'Create Recipient'}</DialogTitle>
          <DialogDescription>
            {isEditMode
              ? 'Update recipient information and notification preferences'
              : 'Create a new recipient for delivery tasks'}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(90vh-120px)]">
          <div className="px-6 pb-6">
            <RecipientForm
              onSubmit={onSubmit}
              isProcessing={isProcessing}
              initialData={recipient}
              mode={isEditMode ? 'edit' : 'create'}
            />
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
});

RecipientFormModal.displayName = 'RecipientFormModal';
